import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { stepUpRequestRef } from './step_up_ref';
import { StepUpMfaModal } from '../../../components/step_up_mfa_modal';
import { ApiClientEndpointsByService, ApiClientService } from '../types/api_client.constants';
/**
 * StepUpProvider owns the step-up MFA modal and exposes `requestStepUp` to the interceptor
 * via a shared ref. It must be a child of ApiClientProvider (so the modal can use useApiClient)
 * and uses useLayoutEffect to ensure the ref is populated before the interceptor fires.
 */
export const StepUpProvider = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [mfaMethods, setMfaMethods] = useState([]);
    const pendingRef = useRef(null);
    const fetchMfaMethods = useCallback(async () => {
        const baseURL = ApiClientEndpointsByService[ApiClientService.IAM];
        const response = await fetch(`${baseURL}/v1/mfa`, {
            credentials: 'include',
            headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
            throw new Error('Failed to fetch MFA methods');
        }
        const body = (await response.json());
        return (body.data || []).map((m) => ({
            id: m.id,
            type: m.type,
            identifier: m.identifier,
        }));
    }, []);
    const requestStepUp = useCallback((methods) => {
        if (pendingRef.current) {
            return new Promise((resolve, reject) => {
                const existing = pendingRef.current;
                const originalResolve = existing.resolve;
                const originalReject = existing.reject;
                pendingRef.current = {
                    resolve: () => {
                        originalResolve();
                        resolve();
                    },
                    reject: (reason) => {
                        originalReject(reason);
                        reject(reason);
                    },
                };
            });
        }
        return new Promise((resolve, reject) => {
            pendingRef.current = { resolve, reject };
            if (methods.length > 0) {
                setMfaMethods(methods);
                setIsOpen(true);
            }
            else {
                void fetchMfaMethods()
                    .then((fetched) => {
                    setMfaMethods(fetched);
                    setIsOpen(true);
                })
                    .catch((err) => {
                    pendingRef.current = null;
                    reject(err);
                });
            }
        });
    }, [fetchMfaMethods]);
    useLayoutEffect(() => {
        stepUpRequestRef.current = requestStepUp;
        return () => {
            if (stepUpRequestRef.current === requestStepUp) {
                stepUpRequestRef.current = null;
            }
            const pending = pendingRef.current;
            pendingRef.current = null;
            pending?.reject(new Error('Step-up provider unmounted'));
        };
    }, [requestStepUp]);
    const handleSuccess = useCallback(() => {
        setIsOpen(false);
        const pending = pendingRef.current;
        pendingRef.current = null;
        pending?.resolve();
    }, []);
    const handleClose = useCallback(() => {
        setIsOpen(false);
        const pending = pendingRef.current;
        pendingRef.current = null;
        pending?.reject(new Error('Step-up cancelled'));
    }, []);
    return (_jsxs(_Fragment, { children: [children, _jsx(StepUpMfaModal, { isOpen: isOpen, onClose: handleClose, onSuccess: handleSuccess, prefetchedMethods: mfaMethods })] }));
};
